import { HttpParams } from '@angular/common/http';
import { Injector, runInInjectionContext, signal } from '@angular/core';
import { Observable, startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import { DateUtilitiesService, ESqlOperators, IFilterIdentity, TFilterSave } from 'scw-digital-boards-widgets';
import { LineAdvancedFilterApiMatcher } from '../../../../../shared/components/advanced-filter/advanced-filter-api-matcher/line-advanced-filter-api-matcher';

export interface IOpenDfPageUrl {
  originIncludes: string;
  url: string;
}

export const openDfPageOptions = {
  productionReview: {
    filters: [
      { field: 'date', operators: [ESqlOperators.$in] },
      { field: 'shiftId', operators: [ESqlOperators.$in] },
      { field: 'lineId', operators: [ESqlOperators.$in] },
    ],
    inferredFilters: (injector, filters): Observable<TFilterSave[]> => {
      const lineId = getValueIfSingleSelect(
        filters.find(({ field }) => field === 'lineId')?.value as number[] | undefined,
      );

      return lineId
        ? new LineAdvancedFilterApiMatcher(
            lineId,
            runInInjectionContext(injector, () => signal({})),
            injector,
          ).data$.pipe(map(({ siteId }) => [{ field: 'siteId', operator: ESqlOperators.$in, value: [siteId] }]))
        : new Observable<TFilterSave[]>().pipe(startWith([]));
    },
    url: (filters, oeeTrackerUrl, dateUtilities): IOpenDfPageUrl => {
      const dateFilter = filters.find(({ field }) => field === 'date');
      const shiftId = getValueIfSingleSelect(
        filters.find(({ field }) => field === 'shiftId')?.value as number[] | undefined,
      );
      const siteId = getValueIfSingleSelect(
        filters.find(({ field }) => field === 'siteId')?.value as number[] | undefined,
      );
      const lineId = getValueIfSingleSelect(
        filters.find(({ field }) => field === 'lineId')?.value as number[] | undefined,
      );

      let httpParams = new HttpParams().set('isNavbarHidden', 'true');

      if (dateFilter) {
        httpParams = httpParams.set('date', dateUtilities.extractValuesFromDateFilter([dateFilter]) as string);
      }

      if (shiftId) {
        httpParams = httpParams.set('shiftId', String(shiftId));
      }

      const siteLine = lineId && siteId ? `/${siteId}/${lineId}` : '';

      const baseUrl = `${oeeTrackerUrl}/#/production-review`;

      return {
        originIncludes: baseUrl,
        url: `${baseUrl}${siteLine}${httpParams.toString() ? `?${httpParams.toString()}` : ''}`,
      };
    },
  },
} satisfies Record<
  string,
  {
    filters: IFilterIdentity[];
    url: (filters: TFilterSave[], oeeTrackerUrl: string, dateUtilities: DateUtilitiesService) => IOpenDfPageUrl;
    inferredFilters?: (injector: Injector, filters: TFilterSave[]) => Observable<TFilterSave[]>;
  }
>;

function getValueIfSingleSelect(value: number[] | undefined): number | undefined {
  return value?.length === 1 ? value[0] : undefined;
}
